import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { FormattedNumberInput } from "@/components/ui/formatted-number-input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { ArrowLeft, Bell, MapPin, Package, X, Save, Trash2 } from "lucide-react";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { updateMemberPreferencesSchema, type MemberPreferences, type UpdateMemberPreferences } from "@shared/schema";
import { EQUIPMENT_TYPES } from "@/lib/equipmentTypes";

export default function PreferencesSettings() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  
  const [newOrigin, setNewOrigin] = useState("");
  const [newDestination, setNewDestination] = useState("");

  const { data: preferences, isLoading } = useQuery<MemberPreferences>({
    queryKey: ["/api/preferences"],
  });

  const form = useForm<UpdateMemberPreferences>({
    resolver: zodResolver(updateMemberPreferencesSchema),
    defaultValues: {
      preferredOrigins: [],
      preferredDestinations: [],
      minWeight: null,
      maxWeight: null,
      equipmentTypes: [],
      matchThreshold: 50,
      notificationsEnabled: true,
      emailNotifications: true,
      smsNotifications: false,
    },
  });

  useEffect(() => {
    if (preferences) {
      form.reset({
        preferredOrigins: preferences.preferredOrigins || [],
        preferredDestinations: preferences.preferredDestinations || [],
        minWeight: preferences.minWeight ?? null,
        maxWeight: preferences.maxWeight ?? null,
        equipmentTypes: preferences.equipmentTypes || [],
        matchThreshold: preferences.matchThreshold ?? 50,
        notificationsEnabled: preferences.notificationsEnabled ?? true,
        emailNotifications: preferences.emailNotifications ?? true,
        smsNotifications: preferences.smsNotifications ?? false,
      });
    }
  }, [preferences, form]);

  const updateMutation = useMutation({
    mutationFn: async (data: UpdateMemberPreferences) => {
      return apiRequest("PUT", "/api/preferences", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/preferences"] });
      toast({
        title: "Preferences saved",
        description: "Your matching preferences have been updated.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to save preferences. Please try again.",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: UpdateMemberPreferences) => {
    updateMutation.mutate(data);
  };

  const addOrigin = () => {
    if (newOrigin.trim()) {
      const currentOrigins = form.getValues("preferredOrigins") || [];
      form.setValue("preferredOrigins", [...currentOrigins, newOrigin.trim()]);
      setNewOrigin("");
    }
  };

  const removeOrigin = (index: number) => {
    const currentOrigins = form.getValues("preferredOrigins") || [];
    const updated = [...currentOrigins];
    updated.splice(index, 1);
    form.setValue("preferredOrigins", updated);
  };

  const addDestination = () => {
    if (newDestination.trim()) {
      const currentDests = form.getValues("preferredDestinations") || [];
      form.setValue("preferredDestinations", [...currentDests, newDestination.trim()]);
      setNewDestination("");
    }
  };

  const removeDestination = (index: number) => {
    const currentDests = form.getValues("preferredDestinations") || [];
    const updated = [...currentDests];
    updated.splice(index, 1);
    form.setValue("preferredDestinations", updated);
  };

  const toggleEquipmentType = (type: string) => {
    const currentTypes = form.getValues("equipmentTypes") || [];
    if (currentTypes.includes(type)) {
      form.setValue("equipmentTypes", currentTypes.filter(t => t !== type));
    } else {
      form.setValue("equipmentTypes", [...currentTypes, type]);
    }
  };

  const clearAllPreferences = () => {
    form.reset({
      preferredOrigins: [],
      preferredDestinations: [],
      minWeight: null,
      maxWeight: null,
      equipmentTypes: [],
      matchThreshold: 50,
      notificationsEnabled: true,
      emailNotifications: true,
      smsNotifications: false,
    });
  };

  if (isLoading) {
    return (
      <div className="container max-w-2xl mx-auto py-8 px-4">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/3"></div>
          <div className="h-48 bg-muted rounded"></div>
          <div className="h-48 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  const watchedOrigins = form.watch("preferredOrigins") || [];
  const watchedDestinations = form.watch("preferredDestinations") || [];
  const watchedEquipmentTypes = form.watch("equipmentTypes") || [];
  const watchedThreshold = form.watch("matchThreshold") ?? 50;

  return (
    <div className="container max-w-2xl mx-auto py-8 px-4">
      <div className="flex items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setLocation("/dashboard")}
            data-testid="button-back"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold" data-testid="text-page-title">Notification Preferences</h1>
            <p className="text-muted-foreground">
              Manage how you receive notifications for messages and load/truck matches
            </p>
          </div>
        </div>
        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={clearAllPreferences}
            data-testid="button-clear-preferences"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Clear All
          </Button>
          <Button
            onClick={form.handleSubmit(onSubmit)}
            disabled={updateMutation.isPending}
            data-testid="button-save-preferences"
          >
            <Save className="h-4 w-4 mr-2" />
            {updateMutation.isPending ? "Saving..." : "Save Preferences"}
          </Button>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Notification Settings
              </CardTitle>
              <CardDescription>
                Control how you receive notifications for new messages and load/truck matches
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="notificationsEnabled"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between">
                    <div>
                      <FormLabel>In-App Notifications</FormLabel>
                      <FormDescription>
                        Get notified in the app when you receive new messages, quotes, or load/truck matches
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value ?? true}
                        onCheckedChange={field.onChange}
                        data-testid="switch-notifications-enabled"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="emailNotifications"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between">
                    <div>
                      <FormLabel>Email Notifications</FormLabel>
                      <FormDescription>
                        Receive email alerts when you get new messages, quotes, or claim invitations
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value ?? false}
                        onCheckedChange={field.onChange}
                        data-testid="switch-email-notifications"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="smsNotifications"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between">
                    <div>
                      <FormLabel>SMS Notifications</FormLabel>
                      <FormDescription>
                        Coming soon - Get text message alerts for important updates
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value ?? false}
                        onCheckedChange={field.onChange}
                        disabled
                        data-testid="switch-sms-notifications"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="matchThreshold"
                render={({ field }) => (
                  <FormItem className="pt-4">
                    <FormLabel>Match Threshold: {watchedThreshold}%</FormLabel>
                    <FormDescription>
                      Only notify me when match score is at least this percentage
                    </FormDescription>
                    <FormControl>
                      <Slider
                        value={[field.value ?? 50]}
                        onValueChange={([value]) => field.onChange(value)}
                        min={10}
                        max={100}
                        step={5}
                        className="w-full"
                        data-testid="slider-match-threshold"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Preferred Locations
              </CardTitle>
              <CardDescription>
                Add cities, states, or regions you're interested in. Supports region names like <span className="font-medium">Southeast</span>, <span className="font-medium">Midwest</span>, <span className="font-medium">West Coast</span>, <span className="font-medium">Northeast</span>, <span className="font-medium">Southwest</span>, <span className="font-medium">Great Lakes</span>, <span className="font-medium">New England</span>, <span className="font-medium">Sun Belt</span>, and more.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <FormLabel>Origin Locations</FormLabel>
                <div className="flex gap-2">
                  <Input
                    placeholder="e.g. Chicago, IL or Midwest"
                    value={newOrigin}
                    onChange={(e) => setNewOrigin(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addOrigin())}
                    data-testid="input-new-origin"
                  />
                  <Button type="button" onClick={addOrigin} variant="secondary" data-testid="button-add-origin">
                    Add
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {watchedOrigins.map((origin, index) => (
                    <Badge key={index} variant="secondary" className="gap-1">
                      {origin}
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="ml-1"
                        onClick={() => removeOrigin(index)}
                        data-testid={`button-remove-origin-${index}`}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </Badge>
                  ))}
                  {watchedOrigins.length === 0 && (
                    <p className="text-sm text-muted-foreground">No origins specified - all origins will match</p>
                  )}
                </div>
              </div>

              <div className="space-y-3">
                <FormLabel>Destination Locations</FormLabel>
                <div className="flex gap-2">
                  <Input
                    placeholder="e.g. Los Angeles, CA or West Coast"
                    value={newDestination}
                    onChange={(e) => setNewDestination(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addDestination())}
                    data-testid="input-new-destination"
                  />
                  <Button type="button" onClick={addDestination} variant="secondary" data-testid="button-add-destination">
                    Add
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {watchedDestinations.map((dest, index) => (
                    <Badge key={index} variant="secondary" className="gap-1">
                      {dest}
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="ml-1"
                        onClick={() => removeDestination(index)}
                        data-testid={`button-remove-destination-${index}`}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </Badge>
                  ))}
                  {watchedDestinations.length === 0 && (
                    <p className="text-sm text-muted-foreground">No destinations specified - all destinations will match</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                Weight & Equipment
              </CardTitle>
              <CardDescription>
                Specify weight ranges and equipment types you handle
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="minWeight"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Minimum Weight (lbs)</FormLabel>
                      <FormControl>
                        <FormattedNumberInput
                          decimals={0}
                          placeholder="0"
                          value={field.value ?? ""}
                          onChange={(val) =>
                            field.onChange(val ? parseInt(val) : null)
                          }
                          data-testid="input-min-weight"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="maxWeight"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Maximum Weight (lbs)</FormLabel>
                      <FormControl>
                        <FormattedNumberInput
                          decimals={0}
                          placeholder="No limit"
                          value={field.value ?? ""}
                          onChange={(val) =>
                            field.onChange(val ? parseInt(val) : null)
                          }
                          data-testid="input-max-weight"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <div className="space-y-3">
                <FormLabel>Equipment Types</FormLabel>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {EQUIPMENT_TYPES.map((type) => (
                    <div key={type} className="flex items-center space-x-2">
                      <Checkbox
                        id={`equipment-${type}`}
                        checked={watchedEquipmentTypes.includes(type)}
                        onCheckedChange={() => toggleEquipmentType(type)}
                        data-testid={`checkbox-equipment-${type.toLowerCase().replace(/\s+/g, '-')}`}
                      />
                      <label htmlFor={`equipment-${type}`} className="text-sm cursor-pointer">{type}</label>
                    </div>
                  ))}
                </div>
                {watchedEquipmentTypes.length === 0 && (
                  <p className="text-sm text-muted-foreground">No equipment types specified - all types will match</p>
                )}
              </div>
            </CardContent>
          </Card>
        </form>
      </Form>
    </div>
  );
}
